import React from 'react';
import { IconThumbUp, IconThumbDown, IconCopy, IconBookmark, IconShare } from './icons';
import { conversationWorkspaceStrings } from '../../i18n/conversationWorkspace';
import { useTheme } from '../../theme/ThemeProvider';

// Pixel-exact port of Figma node 18:148 (OziraResponseActions). Contract
// (spec Gate 5, non-negotiable):
//   - icon-only, no visible text labels
//   - transparent background always
//   - NEVER a colored fill, even for the selected Helpful/NotHelpful state --
//     selection is shown by a heavier (2px) border only, never green/red/brand fill
//   - 44x44 hit target per button, ~20x20 icon, 8px gaps
//   - 1px border in an inverse-of-surface color (light/dark aware via tokens)
//   - hidden entirely while a response is still streaming
// A Share button was added after the original spec (the backend's
// createShare()/resolveShare() grants existed with no UI trigger at all) --
// same icon-only/44x44/no-fill contract, appended after Save.
export type ResponseFeedback = 'helpful' | 'notHelpful' | null;

interface OziraResponseActionsProps {
  feedback: ResponseFeedback;
  onFeedback: (value: 'helpful' | 'notHelpful') => void;
  onCopy: () => void;
  saved: boolean;
  onSave: () => void;
  onRetry: () => void;
  onShare?: () => void;
}

export function OziraResponseActions({ feedback, onFeedback, onCopy, saved, onSave, onRetry, onShare }: OziraResponseActionsProps) {
  const { language } = useTheme();
  const t = (key: string) => conversationWorkspaceStrings[language]?.[key] ?? conversationWorkspaceStrings.en[key] ?? key;

  return (
    <div className="ozira-response-actions" role="group" aria-label={t('more')}>
      <button
        type="button"
        className={`ozira-response-action${feedback === 'helpful' ? ' is-selected' : ''}`}
        aria-pressed={feedback === 'helpful'}
        aria-label={t('helpful')}
        title={t('helpful')}
        onClick={() => onFeedback('helpful')}
      >
        <IconThumbUp />
      </button>
      <button
        type="button"
        className={`ozira-response-action${feedback === 'notHelpful' ? ' is-selected' : ''}`}
        aria-pressed={feedback === 'notHelpful'}
        aria-label={t('notHelpful')}
        title={t('notHelpful')}
        onClick={() => onFeedback('notHelpful')}
      >
        <IconThumbDown />
      </button>
      <button type="button" className="ozira-response-action" aria-label={t('copy')} title={t('copy')} onClick={onCopy}>
        <IconCopy />
      </button>
      <button
        type="button"
        className={`ozira-response-action${saved ? ' is-selected' : ''}`}
        aria-pressed={saved}
        aria-label={saved ? t('unsave') : t('save')}
        title={saved ? t('unsave') : t('save')}
        onClick={onSave}
      >
        <IconBookmark />
      </button>
      <button type="button" className="ozira-response-action" aria-label={t('retry')} title={t('retry')} onClick={onRetry}>
        <span aria-hidden="true" style={{ fontSize: 20, lineHeight: 1 }}>↻</span>
      </button>
      {onShare ? (
        <button type="button" className="ozira-response-action" aria-label={t('share')} title={t('share')} onClick={onShare}>
          <IconShare />
        </button>
      ) : null}
    </div>
  );
}
